import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// Layout Components
import Header from './components/Header';
import Navbar from './components/Navbar';
import MobileHeader from './components/MobileHeader';
import MobileNav from './components/MobileNav';
import MobileDrawer from './components/MobileDrawer';
import TierGate from './components/TierGate';
import MarkToast from './components/MarkToast';
import { useToast } from './components/Toast';
import GlobalSearch from './components/GlobalSearch';
import OnboardingTour from './components/OnboardingTour';
import useIsMobile from './hooks/useIsMobile';

// Eagerly loaded — hot path views
import Home from './views/Home';
import Inventory from './views/Inventory';
import Recipes from './views/Recipes';
import ShoppingList from './views/ShoppingList';
import Settings from './views/Settings';

// Services
import { getSupabase, signOut as supabaseSignOut, saveUserData, loadUserData } from './services/supabase';
import {
  evaluateMarks,
  getLocalEarnedMarks,
  setLocalEarnedMarks,
  recordMarkRemote,
  fetchEarnedMarks,
  recordDailyLog,
  syncDailyLogRemote,
} from './utils/marks';

// Styles
import './App.css';

// Lazy loaded — secondary views
const MealPlanner       = lazy(() => import('./views/MealPlanner'));
const MacroPlanner      = lazy(() => import('./views/MacroPlanner'));
const Budget            = lazy(() => import('./views/Budget'));
const FitnessDashboard  = lazy(() => import('./views/FitnessDashboard'));
const AIRecipe          = lazy(() => import('./views/AIRecipe'));
const BiometricIntake   = lazy(() => import('./views/BiometricIntake'));
const WorkoutGenerator  = lazy(() => import('./views/WorkoutGenerator'));
const MealSynthesizer   = lazy(() => import('./views/MealSynthesizer'));
const UnifiedCalendar   = lazy(() => import('./views/UnifiedCalendar'));
const ProgressiveOverload = lazy(() => import('./views/ProgressiveOverload'));
const Upgrade           = lazy(() => import('./views/Upgrade'));
const Feedback          = lazy(() => import('./views/Feedback'));
const Blog              = lazy(() => import('./views/Blog'));
const Brigade           = lazy(() => import('./views/Brigade'));
const Catalog           = lazy(() => import('./views/Catalog'));
const AdminDashboard    = lazy(() => import('./views/AdminDashboard'));
const CommunityFeed     = lazy(() => import('./views/CommunityFeed'));
const PostCreate        = lazy(() => import('./views/PostCreate'));
const PostDetail        = lazy(() => import('./views/PostDetail'));
const UserProfile       = lazy(() => import('./views/UserProfile'));
const Discover          = lazy(() => import('./views/Discover'));

const PageFallback = () => (
  <div className="page-container">
    <div className="skeleton skeleton-text skeleton-lg" style={{ width: '40%', marginBottom: 24 }} />
    {[1, 2, 3].map(i => (
      <div key={i} className="skeleton-card" style={{ marginBottom: 12 }}>
        <div className="skeleton skeleton-text" style={{ width: '70%' }} />
        <div className="skeleton skeleton-text skeleton-sm" style={{ width: '45%' }} />
      </div>
    ))}
  </div>
);

const AUTOSAVE_DELAY = 3000;

const App = () => {
  const isMobile = useIsMobile();
  const toast = useToast();
  const [searchOpen, setSearchOpen] = useState(false);

  // --- App State ---
  const [darkMode, setDarkMode] = useState(() => JSON.parse(localStorage.getItem('darkMode')) ?? true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const mainRef = useRef(null);
  const lastScrollY = useRef(0);
  const [tier, setTier] = useState(() => localStorage.getItem('userTier') || 'commis');
  const [inventory, setInventory] = useState(() => JSON.parse(localStorage.getItem('inventory')) || []);
  const [recipes, setRecipes] = useState(() => JSON.parse(localStorage.getItem('recipes')) || []);
  const [shoppingList, setShoppingList] = useState(() => JSON.parse(localStorage.getItem('shoppingList')) || []);
  const [blogPosts, setBlogPosts] = useState(() => JSON.parse(localStorage.getItem('blogPosts')) || []);
  const [mealPlan, setMealPlan] = useState(() => JSON.parse(localStorage.getItem('mealPlan')) || {});
  const [budget, setBudget] = useState(() => JSON.parse(localStorage.getItem('budget')) || {});
  const [macroPlanner, setMacroPlanner] = useState(() => JSON.parse(localStorage.getItem('macroPlanner')) || {});
  const [fitnessData, setFitnessData] = useState(() => JSON.parse(localStorage.getItem('fitnessData')) || {});
  const [biometrics, setBiometrics] = useState(() => JSON.parse(localStorage.getItem('biometrics')) || {});
  const [workoutProtocol, setWorkoutProtocol] = useState(() => JSON.parse(localStorage.getItem('workoutProtocol')) || {});
  const [workoutLog, setWorkoutLog] = useState(() => JSON.parse(localStorage.getItem('workoutLog')) || []);

  // --- Onboarding ---
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('hasSeenOnboarding'));
  const dismissOnboarding = () => {
    localStorage.setItem('hasSeenOnboarding', '1');
    setShowOnboarding(false);
  };

  // --- Auth & Sync State ---
  const [user, setUser] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const saveTimer = useRef(null);
  const hasLoadedCloud = useRef(false);

  // --- Brigade (Marks) State ---
  const [pendingMarks, setPendingMarks] = useState([]);
  const [activeMark, setActiveMark] = useState(null);
  const marksHydrated = useRef(false);

  // --- Local Persistence ---
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    localStorage.setItem('userTier', tier);
    localStorage.setItem('inventory', JSON.stringify(inventory));
    localStorage.setItem('recipes', JSON.stringify(recipes));
    localStorage.setItem('shoppingList', JSON.stringify(shoppingList));
    localStorage.setItem('blogPosts', JSON.stringify(blogPosts));
    localStorage.setItem('mealPlan', JSON.stringify(mealPlan));
    localStorage.setItem('budget', JSON.stringify(budget));
    localStorage.setItem('macroPlanner', JSON.stringify(macroPlanner));
    localStorage.setItem('fitnessData', JSON.stringify(fitnessData));
    localStorage.setItem('biometrics', JSON.stringify(biometrics));
    localStorage.setItem('workoutProtocol', JSON.stringify(workoutProtocol));
    localStorage.setItem('workoutLog', JSON.stringify(workoutLog));
    localStorage.setItem('localLastUpdated', new Date().toISOString());
  }, [darkMode, tier, inventory, recipes, shoppingList, blogPosts, mealPlan, budget, macroPlanner, fitnessData, biometrics, workoutProtocol, workoutLog]);

  // --- Mobile scroll-hide header ---
  useEffect(() => {
    if (!isMobile) return;
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => {
      const current = el.scrollTop;
      const delta = current - lastScrollY.current;
      if (Math.abs(delta) > 4) {
        const hiding = current > 60 && delta > 0;
        setHeaderHidden(hiding);
        setNavHidden(hiding);
        lastScrollY.current = current;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  // --- Supabase Auth Listener ---
  useEffect(() => {
    let subscription;
    getSupabase().then((db) => {
      db.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
      });
      const { data } = db.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          hasLoadedCloud.current = false;
        }
      });
      subscription = data.subscription;
    });
    return () => subscription?.unsubscribe();
  }, []);

  // --- Cloud Sync Helpers ---
  const getAppData = useCallback(() => ({
    inventory, recipes, shoppingList, blogPosts, mealPlan,
    budget, macroPlanner, fitnessData, biometrics, workoutProtocol, workoutLog,
    tier,
    lastUpdated: new Date().toISOString(),
  }), [inventory, recipes, shoppingList, blogPosts, mealPlan, budget, macroPlanner, fitnessData, biometrics, workoutProtocol, workoutLog, tier]);

  const applyCloudData = useCallback((d) => {
    // Determine if local data was written after the cloud snapshot.
    // If so, merge arrays so locally-created items not yet synced are preserved.
    const localTs = localStorage.getItem('localLastUpdated');
    const cloudTs = d.lastUpdated;
    const localIsAhead = localTs && cloudTs && localTs > cloudTs;

    // For array collections: union cloud + any local items not present in cloud (by name).
    // Cloud version wins for items that exist in both (it's the last successfully saved state).
    const mergeArray = (cloudArr, setter, key) => {
      if (!cloudArr) return;
      if (!localIsAhead) { setter(cloudArr); return; }
      const local = JSON.parse(localStorage.getItem(key) || '[]');
      const cloudNames = new Set(cloudArr.map((r) => r.name ?? r.id ?? JSON.stringify(r)));
      const localOnly = local.filter((r) => !cloudNames.has(r.name ?? r.id ?? JSON.stringify(r)));
      setter([...cloudArr, ...localOnly]);
    };

    mergeArray(d.recipes, setRecipes, 'recipes');
    mergeArray(d.inventory, setInventory, 'inventory');
    mergeArray(d.shoppingList, setShoppingList, 'shoppingList');
    mergeArray(d.blogPosts, setBlogPosts, 'blogPosts');
    mergeArray(d.workoutLog, setWorkoutLog, 'workoutLog');

    // For object/map collections prefer cloud unless local is demonstrably ahead.
    if (d.mealPlan) setMealPlan(localIsAhead ? { ...d.mealPlan, ...JSON.parse(localStorage.getItem('mealPlan') || '{}') } : d.mealPlan);
    if (d.budget) setBudget(localIsAhead ? { ...d.budget, ...JSON.parse(localStorage.getItem('budget') || '{}') } : d.budget);
    if (d.macroPlanner) setMacroPlanner(localIsAhead ? { ...d.macroPlanner, ...JSON.parse(localStorage.getItem('macroPlanner') || '{}') } : d.macroPlanner);
    if (d.fitnessData) setFitnessData(localIsAhead ? { ...d.fitnessData, ...JSON.parse(localStorage.getItem('fitnessData') || '{}') } : d.fitnessData);
    if (d.biometrics) setBiometrics(localIsAhead ? { ...d.biometrics, ...JSON.parse(localStorage.getItem('biometrics') || '{}') } : d.biometrics);
    if (d.workoutProtocol) setWorkoutProtocol(localIsAhead ? { ...d.workoutProtocol, ...JSON.parse(localStorage.getItem('workoutProtocol') || '{}') } : d.workoutProtocol);

    const justPaid = new URLSearchParams(window.location.search).get('success') === 'true';
    if (d.tier && !justPaid) {
      setTier(d.tier);
      localStorage.setItem('userTier', d.tier);
    }
  }, []);

  // --- Auto-load on sign in ---
  useEffect(() => {
    if (!user || hasLoadedCloud.current) return;
    hasLoadedCloud.current = true;

    const autoLoad = async () => {
      try {
        setSyncStatus('loading');
        const result = await loadUserData();
        if (result?.data) {
          applyCloudData(result.data);
        }
        setSyncStatus('synced');
      } catch {
        setSyncStatus('error');
        toast.show("Couldn't load your data from the cloud", { variant: 'error' });
      }
    };

    autoLoad();
  }, [user, applyCloudData, toast]);

  // --- Hydrate earned marks from cloud (prevents toast spam on new device) ---
  useEffect(() => {
    if (!user) {
      marksHydrated.current = true;
      return;
    }
    fetchEarnedMarks()
      .then((remote) => {
        const localIds = getLocalEarnedMarks();
        const remoteIds = remote.map((m) => m.mark_id);
        const merged = [...new Set([...localIds, ...remoteIds])];
        setLocalEarnedMarks(merged);
      })
      .catch(() => {})
      .finally(() => {
        marksHydrated.current = true;
      });
  }, [user]);

  // --- Evaluate marks on relevant state changes ---
  useEffect(() => {
    const appState = {
      inventory, recipes, workoutLog, mealPlan,
      budget, shoppingList, biometrics,
    };

    recordDailyLog(appState);
    if (user) syncDailyLogRemote(appState).catch(() => {});

    const earned = getLocalEarnedMarks();
    const newlyEarned = evaluateMarks(appState, earned);
    if (newlyEarned.length === 0) return;

    setLocalEarnedMarks([...earned, ...newlyEarned]);

    if (user) {
      newlyEarned.forEach((id) => {
        recordMarkRemote(id).catch(() => {});
      });
    }

    if (marksHydrated.current) {
      setPendingMarks((prev) => [...prev, ...newlyEarned]);
    }
  }, [inventory, recipes, workoutLog, mealPlan, budget, shoppingList, biometrics, user]);

  // --- Process toast queue ---
  useEffect(() => {
    if (activeMark || pendingMarks.length === 0) return;
    setActiveMark(pendingMarks[0]);
    setPendingMarks((prev) => prev.slice(1));
  }, [activeMark, pendingMarks]);

  // --- Debounced autosave ---
  useEffect(() => {
    if (!user || !hasLoadedCloud.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(async () => {
      try {
        setSyncStatus('saving');
        await saveUserData(getAppData());
        setSyncStatus('synced');
      } catch {
        setSyncStatus('error');
        toast.show("Couldn't sync your changes", { variant: 'error' });
      }
    }, AUTOSAVE_DELAY);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [user, getAppData, toast]);

  const handleSignOut = async () => {
    await supabaseSignOut();
    setUser(null);
    setSyncStatus('idle');
    hasLoadedCloud.current = false;
  };

  // --- Global cmd/ctrl + K opens search ---
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // --- App Logic ---
  const addInventoryItem = (item) => setInventory((prev) => [...prev, item]);
  const removeInventoryItem = (index) => setInventory((prev) => prev.filter((_, i) => i !== index));
  const addRecipe = (recipe) => setRecipes([...recipes, recipe]);
  const removeRecipe = (index) => setRecipes(recipes.filter((_, i) => i !== index));
  const addToShoppingList = (name, quantity) => setShoppingList([...shoppingList, { name, quantity: quantity || 1 }]);
  const removeFromShoppingList = (index) => setShoppingList(shoppingList.filter((_, i) => i !== index));
  const clearShoppingList = () => setShoppingList([]);
  const deductFromInventory = (name, qty) => {
    setInventory(prev => prev.map(item =>
      item.name.toLowerCase() === name.toLowerCase()
        ? { ...item, quantity: Math.max(0, item.quantity - qty) }
        : item
    ));
  };

  const appRoutes = (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<Home blogPosts={blogPosts} setBlogPosts={setBlogPosts} inventory={inventory} tier={tier} addToShoppingList={addToShoppingList} addRecipe={addRecipe} earnedMarks={getLocalEarnedMarks()} mealPlan={mealPlan} macroPlanner={macroPlanner} recipes={recipes} user={user} />} />
      <Route path="/inventory" element={<Inventory inventory={inventory} addInventoryItem={addInventoryItem} removeInventoryItem={removeInventoryItem} addToShoppingList={addToShoppingList} recipes={recipes} tier={tier} />} />
      <Route path="/recipes" element={<Recipes recipes={recipes} addRecipe={addRecipe} removeRecipe={removeRecipe} inventory={inventory} deductFromInventory={deductFromInventory} addToShoppingList={addToShoppingList} tier={tier} />} />
      <Route path="/shopping-list" element={<ShoppingList shoppingList={shoppingList} addToShoppingList={addToShoppingList} removeFromShoppingList={removeFromShoppingList} clearShoppingList={clearShoppingList} user={user} tier={tier} />} />
      <Route path="/meal-planner" element={<TierGate tier={tier} path="/meal-planner"><MealPlanner mealPlan={mealPlan} setMealPlan={setMealPlan} recipes={recipes} inventory={inventory} addToShoppingList={addToShoppingList} tier={tier} /></TierGate>} />
      <Route path="/macro-planner" element={<TierGate tier={tier} path="/macro-planner"><MacroPlanner recipes={recipes} macroPlanner={macroPlanner} setMacroPlanner={setMacroPlanner} /></TierGate>} />
      <Route path="/budget" element={<TierGate tier={tier} path="/budget"><Budget shoppingList={shoppingList} budget={budget} setBudget={setBudget} addToShoppingList={addToShoppingList} /></TierGate>} />
      <Route path="/fitness" element={<TierGate tier={tier} path="/fitness"><FitnessDashboard fitnessData={fitnessData} setFitnessData={setFitnessData} /></TierGate>} />
      <Route path="/biometrics" element={<TierGate tier={tier} path="/biometrics"><BiometricIntake biometrics={biometrics} setBiometrics={setBiometrics} /></TierGate>} />
      <Route path="/workout-protocol" element={<TierGate tier={tier} path="/workout-protocol"><WorkoutGenerator biometrics={biometrics} workoutProtocol={workoutProtocol} setWorkoutProtocol={setWorkoutProtocol} /></TierGate>} />
      <Route path="/meal-synthesizer" element={<TierGate tier={tier} path="/meal-synthesizer"><MealSynthesizer biometrics={biometrics} workoutProtocol={workoutProtocol} recipes={recipes} mealPlan={mealPlan} setMealPlan={setMealPlan} macroPlanner={macroPlanner} setMacroPlanner={setMacroPlanner} /></TierGate>} />
      <Route path="/calendar" element={<TierGate tier={tier} path="/calendar"><UnifiedCalendar mealPlan={mealPlan} setMealPlan={setMealPlan} workoutProtocol={workoutProtocol} setWorkoutProtocol={setWorkoutProtocol} recipes={recipes} biometrics={biometrics} /></TierGate>} />
      <Route path="/progressive-overload" element={<TierGate tier={tier} path="/progressive-overload"><ProgressiveOverload workoutProtocol={workoutProtocol} setWorkoutProtocol={setWorkoutProtocol} workoutLog={workoutLog} setWorkoutLog={setWorkoutLog} biometrics={biometrics} tier={tier} earnedMarks={getLocalEarnedMarks()} /></TierGate>} />
      <Route path="/ai-recipe" element={<TierGate tier={tier} path="/ai-recipe"><AIRecipe addRecipe={addRecipe} tier={tier} /></TierGate>} />
      <Route path="/upgrade" element={<Upgrade tier={tier} setTier={setTier} user={user} />} />
      <Route path="/feedback" element={<Feedback user={user} />} />
      <Route path="/catalog" element={<Catalog addRecipe={addRecipe} />} />
      <Route path="/admin" element={<AdminDashboard user={user} />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<Blog />} />
      <Route path="/brigade" element={<Brigade user={user} appState={{ inventory, recipes, workoutLog, mealPlan, budget, shoppingList, biometrics }} />} />
      <Route path="/settings" element={<Settings user={user} syncStatus={syncStatus} signOut={handleSignOut} tier={tier} inventory={inventory} recipes={recipes} shoppingList={shoppingList} budget={budget} workoutLog={workoutLog} />} />
      {/* Community Routes */}
      <Route path="/community" element={<CommunityFeed user={user} addRecipe={addRecipe} />} />
      <Route path="/community/new" element={<PostCreate user={user} recipes={recipes} />} />
      <Route path="/community/post/:postId" element={<PostDetail user={user} addRecipe={addRecipe} />} />
      <Route path="/community/user/:userId" element={<UserProfile user={user} />} />
      <Route path="/profile" element={<UserProfile user={user} />} />
      <Route path="/discover" element={<Discover user={user} />} />
    </Routes>
    </Suspense>
  );

  if (isMobile) {
    return (
      <div className={`app-root mobile-layout ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <MobileHeader user={user} syncStatus={syncStatus} onMenuOpen={() => setDrawerOpen(true)} hidden={headerHidden} />
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} tier={tier} darkMode={darkMode} setDarkMode={setDarkMode} />
        <main id="main-content" className="mobile-main" ref={mainRef}>
          {appRoutes}
        </main>
        <MobileNav tier={tier} hidden={navHidden} />
        <MarkToast markId={activeMark} onDismiss={() => setActiveMark(null)} />
        <GlobalSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          recipes={recipes}
          inventory={inventory}
        />
        {showOnboarding && <OnboardingTour onDismiss={dismissOnboarding} />}
      </div>
    );
  }

  return (
    <div className={`app-root ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header
        user={user}
        syncStatus={syncStatus}
        sidebarOpen={sidebarOpen}
        toggleSidebar={() => setSidebarOpen(o => !o)}
      />
      <div className="app-body">
        {sidebarOpen && window.innerWidth < 768 && (
          <div className="nav-backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
        )}
        <Navbar
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          sidebarOpen={sidebarOpen}
          closeSidebar={() => setSidebarOpen(false)}
          tier={tier}
        />
        <main id="main-content" className="app-main">
          {appRoutes}
        </main>
      </div>
      <MarkToast markId={activeMark} onDismiss={() => setActiveMark(null)} />
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        recipes={recipes}
        inventory={inventory}
      />
      {showOnboarding && <OnboardingTour onDismiss={dismissOnboarding} />}
    </div>
  );
};

export default App;
